import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { updateProfile, changePassword, getNotificationPreferences, updateNotificationPreferences, getUsageSummary } from '../api';

const USAGE_FIELDS = [
  { key: 'saved_strategies', label: 'Saved strategies' },
  { key: 'backtests_per_day', label: 'Backtests today' },
  { key: 'ai_generations_per_day', label: 'AI strategy builds today' },
  { key: 'ai_explanations_per_day', label: 'AI explanations today' },
  { key: 'analyst_conversations_per_day', label: 'AI Analyst conversations today' },
  { key: 'analyst_messages_per_day', label: 'AI Analyst messages today' },
  { key: 'builder_chat_conversations_per_day', label: 'AI strategy chats today' },
  { key: 'builder_chat_messages_per_day', label: 'AI strategy chat messages today' },
  { key: 'exports_per_day', label: 'Strategy exports today' },
];

export default function ProfilePage() {
  const { user, tier, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Profile editing
  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [profileErrors, setProfileErrors] = useState({});

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [pwErrors, setPwErrors] = useState({});

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifMsg, setNotifMsg] = useState(null);

  // Usage vs. quota
  const [usage, setUsage] = useState(null);
  const [usageErr, setUsageErr] = useState(false);

  const NOTIF_OPTIONS = [
    { key: 'trade_executed', label: 'Trade executed', desc: 'Buy or sell signal triggered' },
    { key: 'daily_summary', label: 'Daily summary', desc: 'End of day portfolio update' },
    { key: 'weekly_report', label: 'Weekly performance report', desc: 'Sunday evening' },
    { key: 'weekly_market_recap', label: 'Weekly market recap', desc: 'Index moves, macro regime, and top news from the past week' },
    { key: 'market_alerts', label: 'Market alerts', desc: 'Relevant news for your strategy assets' },
    { key: 'strategy_suggestions', label: 'Strategy suggestions', desc: 'AI detected new opportunities' },
    { key: 'onboarding_emails', label: 'Getting-started emails', desc: 'A few tips to help you run your first backtest and take a strategy live' },
  ];

  useEffect(() => {
    getNotificationPreferences()
      .then(({ data }) => setNotifPrefs(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getUsageSummary()
      .then(({ data }) => setUsage(data.usage))
      .catch(() => setUsageErr(true));
  }, []);

  const handleNotifToggle = (key) => {
    setNotifPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNotifSave = async () => {
    setNotifSaving(true);
    setNotifMsg(null);
    try {
      const { data } = await updateNotificationPreferences(notifPrefs);
      setNotifPrefs(data);
      setNotifMsg('Preferences saved.');
    } catch {
      setNotifMsg('Failed to save preferences.');
    } finally {
      setNotifSaving(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setProfileErrors({});
    setProfileSaving(true);
    try {
      await updateProfile({ username, email });
      await refreshUser();
      setProfileMsg('Profile updated.');
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object' && !data.detail) {
        setProfileErrors(data);
      } else {
        setProfileMsg(data?.detail || err.message);
      }
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    setPwErrors({});
    if (newPassword !== confirmPassword) {
      setPwErrors({ confirm_password: ['Passwords do not match.'] });
      return;
    }
    setPwSaving(true);
    try {
      const res = await changePassword({ current_password: currentPassword, new_password: newPassword });
      setPwMsg(res.data.detail);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object' && !data.detail) {
        setPwErrors(data);
      } else {
        setPwMsg(data?.detail || err.message);
      }
    } finally {
      setPwSaving(false);
    }
  };

  const tierConfig = {
    free: { label: 'Free', color: 'var(--muted)', desc: 'Basic access to default strategies and limited backtesting.' },
    basic: { label: 'Basic', color: 'var(--blue)', desc: 'Unlimited strategies, higher daily limits on backtests and AI tools.' },
    pro: { label: 'Pro', color: 'var(--teal)', desc: 'Everything in Basic, unlimited AI tools and backtests, plus priority support.' },
  };
  const currentTier = tierConfig[tier] || tierConfig.free;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: 24 }}>
      <Helmet>
        <title>Profile — Algovex</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* ── Subscription Card ── */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
          Subscription
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <span style={{
            padding: '4px 12px', fontSize: 13, fontWeight: 700,
            background: currentTier.color, color: 'var(--bg)',
            borderRadius: 6, textTransform: 'uppercase',
          }}>
            {currentTier.label}
          </span>
          <span style={{ fontSize: 13, color: 'var(--muted)', fontFamily: 'var(--font-mono)' }}>
            Member since {new Date(user?.date_joined || Date.now()).toLocaleDateString()}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
          {currentTier.desc}
        </div>
        {tier === 'free' && (
          <button
            onClick={() => navigate('/pricing')}
            style={{
              marginTop: 12, background: 'none', border: '1px solid var(--teal)',
              color: 'var(--teal)', padding: '6px 14px', borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            View plans &amp; upgrade →
          </button>
        )}
        {tier !== 'free' && (
          <button
            onClick={() => navigate('/billing')}
            style={{
              marginTop: 12, background: 'none', border: '1px solid var(--border)',
              color: 'var(--muted)', padding: '6px 14px', borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Manage billing →
          </button>
        )}
      </div>

      {/* ── Usage vs. Quota ── */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
          Usage Today
        </div>
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
          Where you stand against your {currentTier.label} plan's limits.
        </p>

        {usageErr && (
          <div style={{ fontSize: 13, color: 'var(--red)' }}>Couldn't load usage right now.</div>
        )}

        {!usage && !usageErr && (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</div>
        )}

        {usage && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {USAGE_FIELDS.map(({ key, label }) => {
              const entry = usage[key];
              if (!entry) return null;
              const { current, limit } = entry;
              const unlimited = limit === -1;
              const pct = unlimited ? 0 : Math.min(100, limit > 0 ? (current / limit) * 100 : 100);
              const atLimit = !unlimited && current >= limit;
              const barColor = atLimit ? 'var(--red)' : pct >= 80 ? '#e0a92f' : 'var(--teal)';
              return (
                <div key={key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: 'var(--text)' }}>{label}</span>
                    <span style={{ color: atLimit ? 'var(--red)' : 'var(--muted)', fontFamily: 'var(--font-mono)' }}>
                      {current} / {unlimited ? '∞' : limit}
                    </span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--surface)', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      width: unlimited ? '100%' : `${pct}%`,
                      background: unlimited ? 'var(--border)' : barColor,
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Profile Details ── */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
          Account Details
        </div>
        <form onSubmit={handleProfileSave}>
          <label style={labelStyle}>Username</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            style={inputStyle}
            autoComplete="username"
          />
          <FieldError messages={profileErrors.username} />

          <label style={labelStyle}>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            autoComplete="email"
          />
          <FieldError messages={profileErrors.email} />

          {profileMsg && (
            <div style={{ fontSize: 13, marginTop: 8, color: profileErrors.username || profileErrors.email ? 'var(--red)' : 'var(--green)' }}>
              {profileMsg}
            </div>
          )}

          <button type="submit" disabled={profileSaving} style={btnStyle}>
            {profileSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* ── Change Password ── */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
          Change Password
        </div>
        <form onSubmit={handlePasswordChange}>
          <label style={labelStyle}>Current Password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            style={inputStyle}
            autoComplete="current-password"
          />
          <FieldError messages={pwErrors.current_password} />

          <label style={labelStyle}>New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            style={inputStyle}
            autoComplete="new-password"
          />
          <FieldError messages={pwErrors.new_password} />
          {!pwErrors.new_password && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
              At least 8 characters. Can't be entirely numeric or a commonly used password.
            </div>
          )}

          <label style={labelStyle}>Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={8}
            style={inputStyle}
            autoComplete="new-password"
          />
          <FieldError messages={pwErrors.confirm_password} />

          {pwMsg && (
            <div style={{ fontSize: 13, marginTop: 8, color: Object.keys(pwErrors).length > 0 ? 'var(--red)' : 'var(--green)' }}>
              {pwMsg}
            </div>
          )}

          <button type="submit" disabled={pwSaving} style={btnStyle}>
            {pwSaving ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* ── Notification Preferences ── */}
      <div style={sectionStyle}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
          Notification Preferences
        </div>
        <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
          Control which notifications you receive for paper trading activity.
        </p>

        {notifPrefs && (
          <>
            {/* Channel toggles */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {[{ key: 'email_enabled', label: '📧 Email' }, { key: 'inapp_enabled', label: '🔔 In-App' }].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleNotifToggle(key)}
                  style={{
                    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    background: notifPrefs[key] ? 'rgba(47,214,195,0.15)' : 'var(--surface)',
                    color: notifPrefs[key] ? 'var(--teal)' : 'var(--muted)',
                    border: `1px solid ${notifPrefs[key] ? 'var(--teal)' : 'var(--border)'}`,
                  }}
                >
                  {label} {notifPrefs[key] ? '✓' : '○'}
                </button>
              ))}
            </div>

            {/* Notification type toggles */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
              {NOTIF_OPTIONS.map(({ key, label, desc }) => (
                <label
                  key={key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px',
                    background: notifPrefs[key] ? 'rgba(47,214,195,0.07)' : 'var(--surface)',
                    border: `1px solid ${notifPrefs[key] ? 'rgba(47,214,195,0.4)' : 'var(--border)'}`,
                    borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!notifPrefs[key]}
                    onChange={() => handleNotifToggle(key)}
                    style={{ marginTop: 2, accentColor: 'var(--teal)' }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {notifMsg && (
              <div style={{ fontSize: 13, marginBottom: 10, color: notifMsg.includes('Failed') ? 'var(--red)' : 'var(--green)' }}>
                {notifMsg}
              </div>
            )}
            <button onClick={handleNotifSave} disabled={notifSaving} style={btnStyle}>
              {notifSaving ? 'Saving…' : 'Save Preferences'}
            </button>
          </>
        )}

        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => navigate('/notifications')}
            style={{
              background: 'none', border: '1px solid var(--border)',
              color: 'var(--teal)', padding: '6px 14px', borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            View notification history →
          </button>
        </div>
      </div>
    </div>
  );
}

const sectionStyle = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 20,
  marginBottom: 16,
};

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: 'var(--text)', marginTop: 14, marginBottom: 4,
};

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 14,
  border: '1px solid var(--border)', borderRadius: 6, boxSizing: 'border-box',
  background: 'var(--surface)', color: 'var(--text)',
};

const btnStyle = {
  marginTop: 16, padding: '10px 20px',
  fontSize: 13, fontWeight: 700,
  background: 'var(--teal)', color: 'var(--bg)',
  border: 'none', borderRadius: 8, cursor: 'pointer',
};

function FieldError({ messages }) {
  if (!messages || messages.length === 0) return null;
  return (
    <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
      {[].concat(messages).map((msg, i) => <div key={i}>{msg}</div>)}
    </div>
  );
}
